import * as React from 'react'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  TIMELINE_LABEL_MAX,
  TIMELINE_LABEL_TOO_LONG,
  timelineLabelSchema,
} from '@lib/input/event-timeline.input'

/**
 * Why a description cannot be saved, in the schema's own words.
 *
 * Empty is not one of them: a blank box is the field's own empty state, and
 * the button that would save it is already disabled. A description over the
 * cap is the one the person has to be told about, because the API will refuse
 * it and used to be the first place they found out.
 */
export function timelineLabelError(value: string): string | null {
  if (value.trim() === '') return null
  const parsed = timelineLabelSchema.safeParse(value)
  if (parsed.success) return null
  return parsed.error.issues[0]?.message ?? TIMELINE_LABEL_TOO_LONG
}

/** The counter appears from here, so it is on screen before the cap matters. */
const COUNT_FROM = TIMELINE_LABEL_MAX - 100

/** How long a moment's name runs in a button's or a field's label. */
const NAME_LENGTH = 60

/**
 * A moment's description as a short name, for what labels a control: its first
 * line, cut to a few words. A description can run to several lines now, and
 * "Remove" followed by a paragraph is no name for a button.
 */
export function momentName(label: string): string {
  const first = label.trim().split('\n')[0]?.trim() ?? ''
  return first.length > NAME_LENGTH ? `${first.slice(0, NAME_LENGTH - 1).trimEnd()}…` : first
}

/**
 * The description of one moment, on an event's day or a venue's template.
 *
 * A box that grows with what is typed, one line to start: a moment can be a
 * name or a paragraph of who stands where, and a planner ran out of room at the
 * old cap of 200 characters. Enter starts a new line, except where `onKeyDown`
 * says otherwise.
 *
 * The API refuses anything over {@link TIMELINE_LABEL_MAX}, and used to be the
 * first place anyone learned that: the box took the paste, the save failed,
 * and Sentry filed "Failed to add a timeline entry" for a sentence that was
 * too long. Typing stops at the cap. A paste that runs past it stays in the
 * box — cutting it off would lose the end with no explanation, which is the
 * failure — and the schema's own message says why it will not save.
 */
export function TimelineLabelField({
  value,
  onChange,
  onBlur,
  onKeyDown,
  size = 'default',
  placeholder,
  'aria-label': ariaLabel,
  id,
  autoFocus,
  className,
}: {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
  size?: 'default' | 'sm'
  placeholder?: string
  'aria-label': string
  id?: string
  autoFocus?: boolean
  className?: string
}) {
  const messageId = React.useId()
  const box = React.useRef<HTMLTextAreaElement>(null)
  const problem = timelineLabelError(value)
  const count = value.trim().length
  const showCount = problem !== null || count >= COUNT_FROM

  // Grown to fit what is in it, so a long moment reads whole without a scroll
  // box inside the list. `field-sizing: content` would do this in CSS, and not
  // yet in every browser people plan weddings in.
  React.useLayoutEffect(() => {
    const el = box.current
    if (el === null) return
    el.style.height = 'auto'
    el.style.height = `${String(el.scrollHeight + el.offsetHeight - el.clientHeight)}px`
  }, [value])

  return (
    <div className={cn('min-w-0 flex-1', className)}>
      <Textarea
        ref={box}
        rows={1}
        value={value}
        maxLength={TIMELINE_LABEL_MAX}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData('text')
          if (pasted === '') return
          // Taken over from the browser so a paste longer than the cap is kept
          // whole. `maxLength` would cut it at the cap and the person would save
          // a description that ends mid-word, with no idea the rest was dropped.
          e.preventDefault()
          const el = e.currentTarget
          const start = el.selectionStart ?? value.length
          const end = el.selectionEnd ?? value.length
          onChange(value.slice(0, start) + pasted + value.slice(end))
          const caret = start + pasted.length
          window.requestAnimationFrame(() => {
            el.setSelectionRange(caret, caret)
          })
        }}
        className={cn(
          'w-full resize-none overflow-hidden',
          // The heights of `Input`'s two sizes, for one line.
          size === 'sm' ? 'min-h-[30px] px-2.5 py-[5px] text-control' : 'min-h-[38px] py-[8px] leading-5',
        )}
        {...(id !== undefined ? { id } : {})}
        {...(autoFocus !== undefined ? { autoFocus } : {})}
        {...(onBlur !== undefined ? { onBlur } : {})}
        {...(onKeyDown !== undefined ? { onKeyDown } : {})}
        {...(problem !== null ? { 'aria-invalid': true } : {})}
        {...(showCount ? { 'aria-describedby': messageId } : {})}
      />
      {showCount ? (
        <p
          id={messageId}
          className={cn('mt-1 text-xs', problem !== null ? 'text-danger-ink' : 'text-ink-muted')}
        >
          {problem !== null ? <span>{problem} </span> : null}
          <span className="tabular-nums">
            {String(count)}/{String(TIMELINE_LABEL_MAX)}
          </span>
        </p>
      ) : null}
    </div>
  )
}
